import classNames from 'classnames';
import React from 'react';
import { useQuery } from '@apollo/client';
import { Delta } from 'quill/core';
import { Link } from 'react-router-dom';
import styles from './CardContent.module.scss';
import useIsPublicView from '../../utils/useIsPublicView';
import { parsePostMentionId } from '../utils/matchMention';
import DeltaPreview from '../../components/DeltaPreview';
import prepareContentForCard from './prepareContentForCard';
import postPath from '../../utils/url/postPath';
import { gql } from '../../graphql/generated/gql';

gql(`
  fragment CardPostContent_post on PublicPostFields {
    id
    content
    version
    ... on PublicPost {
      banner {
        original
      }
    }
    ... on Post {
      banner {
        original
      }
    }
  }
`);

const CARD_POST_CONTENT_QUERY = gql(`
  query cardPostContent($id: ID!) {
    post(id: $id) {
      id
      ...CardPostContent_post
    }
  }
`);

const CARD_PUBLIC_POST_CONTENT_QUERY = gql(`
  query cardPublicPostContent($id: ID!) {
    publicPost(id: $id) {
      id
      ...CardPostContent_post
    }
  }
`);

function getFirstLineFromDelta(delta: Delta): Delta | null {
  let foundLine: Delta | null = null;
  delta.eachLine((line) => {
    foundLine = line;
    return false;
  });
  return foundLine;
}

function hasBlankTitle(delta: Delta | null): boolean {
  if (!delta) return false;
  const firstLine = getFirstLineFromDelta(delta);
  return firstLine?.length() === 0;
}

const CardPostContent = ({ id }: { id: string }) => {
  const isPublicView = useIsPublicView();
  const { id: postId } = parsePostMentionId(id);

  const { data } = useQuery(CARD_POST_CONTENT_QUERY, {
    variables: { id: postId },
    skip: isPublicView,
  });

  const { data: publicData } = useQuery(CARD_PUBLIC_POST_CONTENT_QUERY, {
    variables: { id: postId },
    skip: !isPublicView,
  });

  const post = isPublicView ? publicData?.publicPost : data?.post;
  const content = post?.content ? new Delta(post.content) : null;
  const contentForCard = content ? prepareContentForCard(content) : null;
  const isTitleBlank = hasBlankTitle(content);

  return (
    <div className={styles.contentContainer}>
      <div
        className={classNames(styles.content, {
          [styles.isTitleBlank]: isTitleBlank,
        })}
      >
        {contentForCard && (
          <DeltaPreview
            maxLines={4}
            content={contentForCard}
            typography="card"
          />
        )}
      </div>
      {post?.banner?.original && (
        <div
          className={styles.banner}
          style={{ backgroundImage: `url(${post.banner.original})` }}
        />
      )}
    </div>
  );
};

interface CardContentProps {
  id: string;
  link: boolean;
}

const CardContent = ({ id, link }: CardContentProps) => {
  const content = <CardPostContent id={id} />;
  return (
    <div className="card-content">
      <div className={classNames(styles.container, { [styles.isLink]: link })}>
        {link ? (
          <Link to={postPath({ id, title: '' })}>{content}</Link>
        ) : (
          content
        )}
      </div>
    </div>
  );
};

export default CardContent;
